import React, { useState, useEffect } from 'react';
import { 
  Settings as SettingsIcon, 
  Users, 
  Shield, 
  Check, 
  X, 
  Trash2,
  UserCheck,
  UserX,
  Clock,
  Mail,
  Phone,
  Briefcase,
  AlertCircle,
  CheckCircle,
  User,
  Church,
  Smartphone,
  Bell // 🆕 Ajout de l'icône Bell pour les notifications
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useData } from '../../contexts/DataContext';
import { useNotifications } from '../../contexts/NotificationContext';
import { hasPermission } from '../../utils/helpers';

// Import des composants d'onglets
import ProfileTab from './ProfileTab';
import ModulePermissionsTab from './ModulePermissionsTab';
import ChurchSettingsTab from './ChurchSettingsTab';
import SecurityTab from './SecurityTab';
import PWATab from './PWATab';
import NotificationSettings from './NotificationSettings'; // 🆕 Import du nouveau composant
import { logger, performance, logFirebaseError } from '../../utils/logger';


const Settings = () => {
  const { currentUser } = useAuth();
  const { data, approveUser, rejectUser, updateUserRole, deleteUser } = useData();
  const { notifications, deleteNotificationsByUserId } = useNotifications();

  const [activeTab, setActiveTab] = useState('profile');
  const [pendingUsers, setPendingUsers] = useState([]);
  const [approvedUsers, setApprovedUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState(null);

  // Mettre à jour les listes d'utilisateurs quand data.users change
  useEffect(() => {
    const pending = data.users.filter(u => !u.approved);
    const approved = data.users.filter(u => u.approved);
    
    setPendingUsers(pending);
    setApprovedUsers(approved);
    
    logger.log('📊 Utilisateurs mis à jour:', {
      total: data.users.length,
      pending: pending.length,
      approved: approved.length
    });
  }, [data.users]);

  // Afficher un message temporaire
  const showMessage = (type, text) => {
    setMessage({ type, text });
    setTimeout(() => setMessage(null), 5000);
  };

  const handleApprove = async (userId) => {
    setLoading(true);
    logger.log('✅ Début approbation utilisateur:', userId);
    
    try {
      const result = await approveUser(userId);
      logger.log('📝 Résultat approbation:', result);
      
      if (result.success) {
        const updatedPending = pendingUsers.filter(u => u.id !== userId);
        const approvedUser = pendingUsers.find(u => u.id === userId);
        if (approvedUser) {
          setPendingUsers(updatedPending);
          setApprovedUsers([...approvedUsers, { ...approvedUser, approved: true }]);
        }

        logger.log('🗑️ Suppression des notifications pour userId:', userId);
        const deleteResult = await deleteNotificationsByUserId(userId);
        
        if (deleteResult.success) {
          logger.log(`✅ ${deleteResult.count} notification(s) supprimée(s)`);
        }

        showMessage('success', '✅ Utilisateur approuvé avec succès !');
        logger.log('✅ Approbation réussie et notifications nettoyées');
      } else {
        showMessage('error', '❌ Erreur : ' + result.message);
        logger.error('❌ Erreur approbation:', result.message);
      }
    } catch (error) {
      logger.error('❌ Erreur handleApprove:', error);
      showMessage('error', '❌ Erreur lors de l\'approbation');
    } finally {
      setLoading(false);
    }
  };

  const handleReject = async (userId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir rejeter cet utilisateur ? Cette action est irréversible.')) {
      return;
    }

    setLoading(true);
    logger.log('❌ Début rejet utilisateur:', userId);
    
    try {
      const result = await rejectUser(userId);
      logger.log('📝 Résultat rejet:', result);
      
      if (result.success) {
        setPendingUsers(pendingUsers.filter(u => u.id !== userId));

        logger.log('🗑️ Suppression des notifications pour userId:', userId);
        const deleteResult = await deleteNotificationsByUserId(userId);
        
        if (deleteResult.success) {
          logger.log(`✅ ${deleteResult.count} notification(s) supprimée(s)`);
        }

        showMessage('success', '✅ Utilisateur rejeté avec succès');
        logger.log('✅ Rejet réussi et notifications nettoyées');
      } else {
        showMessage('error', '❌ Erreur : ' + result.message);
        logger.error('❌ Erreur rejet:', result.message);
      }
    } catch (error) {
      logger.error('❌ Erreur handleReject:', error);
      showMessage('error', '❌ Erreur lors du rejet');
    } finally {
      setLoading(false);
    }
  };

  const handleRoleChange = async (userId, newRole) => {
    setLoading(true);
    logger.log('🔄 Changement rôle:', userId, '→', newRole);
    
    try {
      const result = await updateUserRole(userId, newRole);
      logger.log('📝 Résultat changement rôle:', result);
      
      if (result.success) {
        showMessage('success', `✅ Rôle modifié en ${newRole}`);
        logger.log('✅ Changement rôle réussi');
        
        setApprovedUsers(approvedUsers.map(u => 
          u.id === userId ? { ...u, role: newRole } : u
        ));
      } else {
        showMessage('error', '❌ Erreur : ' + result.message);
      }
    } catch (error) {
      logger.error('❌ Erreur handleRoleChange:', error);
      showMessage('error', '❌ Erreur lors du changement de rôle');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (userId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cet utilisateur ? Cette action est irréversible.')) {
      return;
    }

    setLoading(true);
    
    try {
      const result = await deleteUser(userId);
      
      if (result.success) {
        setApprovedUsers(approvedUsers.filter(u => u.id !== userId));

        logger.log('🗑️ Suppression des notifications pour userId:', userId);
        const deleteResult = await deleteNotificationsByUserId(userId);
        
        if (deleteResult.success) {
          logger.log(`✅ ${deleteResult.count} notification(s) supprimée(s)`);
        }

        showMessage('success', '✅ Utilisateur supprimé avec succès');
      } else {
        showMessage('error', '❌ Erreur : ' + result.message);
      }
    } catch (error) {
      logger.error('❌ Erreur handleDelete:', error);
      showMessage('error', '❌ Erreur lors de la suppression');
    } finally {
      setLoading(false);
    }
  };

  const getRoleColor = (role) => {
    switch (role) {
      case 'admin': return 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400';
      case 'president': return 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400';
      case 'pasteur': return 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900/30 dark:text-indigo-400';
      case 'tresorier': return 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400';
      case 'secretaire': return 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400';
      default: return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300';
    }
  };

  const getRoleLabel = (role) => {
    switch (role) {
      case 'admin': return 'Administrateur';
      case 'president': return 'Président';
      case 'pasteur': return 'Pasteur';
      case 'tresorier': return 'Trésorier';
      case 'secretaire': return 'Secrétaire';
      default: return 'Membre';
    }
  };

  // 🆕 MODIFICATION : Ajouter l'onglet Notifications
  const tabs = [
    { 
      id: 'profile', 
      label: 'Mon Profil', 
      icon: User, 
      permission: null
    },
    { 
      id: 'security', 
      label: 'Sécurité', 
      icon: Shield, 
      permission: null
    },
    { 
      id: 'notifications', // 🆕 NOUVEL ONGLET
      label: 'Notifications', 
      icon: Bell, 
      permission: null // Accessible à tous
    },
    { 
      id: 'pwa', 
      label: 'Application', 
      icon: Smartphone, 
      permission: null
    },
    { 
      id: 'users', 
      label: 'Utilisateurs', 
      icon: Users, 
      permission: 'manage_users',
      badge: pendingUsers.length
    },
    { 
      id: 'permissions', 
      label: 'Permissions', 
      icon: Shield, 
      permission: 'manage_users'
    },
    { 
      id: 'church', 
      label: 'Église', 
      icon: Church, 
      permission: 'manage_users'
    }
  ];

  const visibleTabs = tabs.filter(tab => 
    !tab.permission || hasPermission(currentUser, tab.permission)
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-gray-800 dark:text-white flex items-center gap-3">
          <SettingsIcon size={32} />
          Paramètres
        </h1>
        <p className="text-gray-600 dark:text-gray-400 mt-1">
          Gérez vos préférences et les paramètres de l'application
        </p>
      </div>

      {/* Message de feedback */}
      {message && (
        <div className={`p-4 rounded-lg border ${
          message.type === 'success' 
            ? 'bg-green-50 border-green-200 dark:bg-green-900/20 dark:border-green-800' 
            : 'bg-red-50 border-red-200 dark:bg-red-900/20 dark:border-red-800'
        }`}>
          <div className="flex items-center gap-2">
            {message.type === 'success' ? (
              <CheckCircle className="text-green-600 dark:text-green-400" size={20} />
            ) : (
              <AlertCircle className="text-red-600 dark:text-red-400" size={20} />
            )}
            <p className={message.type === 'success' 
              ? 'text-green-800 dark:text-green-200' 
              : 'text-red-800 dark:text-red-200'
            }>
              {message.text}
            </p>
          </div>
        </div>
      )}

      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm">
        {/* Tabs */}
        <div className="border-b border-gray-200 dark:border-gray-700">
          <nav className="flex flex-wrap">
            {visibleTabs.map(tab => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`px-6 py-4 text-sm font-medium border-b-2 transition-colors relative ${
                    activeTab === tab.id
                      ? 'border-blue-600 text-blue-600 dark:text-blue-400'
                      : 'border-transparent text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200'
                  }`}
                >
                  <div className="flex items-center gap-2">
                    <Icon size={18} />
                    {tab.label}
                    {tab.badge > 0 && (
                      <span className="ml-2 px-2 py-0.5 bg-red-500 text-white text-xs rounded-full">
                        {tab.badge}
                      </span>
                    )}
                  </div>
                </button>
              );
            })}
          </nav>
        </div>

        {/* Content */}
        <div className="p-6">
          {/* Onglet Profil */}
          {activeTab === 'profile' && (
            <ProfileTab currentUser={currentUser} showMessage={showMessage} />
          )}

          {/* Onglet Sécurité */}
          {activeTab === 'security' && (
            <SecurityTab />
          )}

          {/* 🆕 NOUVEL ONGLET : Notifications */}
          {activeTab === 'notifications' && (
            <NotificationSettings />
          )}

          {/* Onglet PWA/Application */}
          {activeTab === 'pwa' && (
            <PWATab />
          )}

          {/* Onglet Utilisateurs - code existant continue... */}
          {activeTab === 'users' && hasPermission(currentUser, 'manage_users') && (
            <div className="space-y-8">
              {/* Utilisateurs en attente */}
              <div>
                <div className="flex items-center gap-2 mb-4">
                  <Clock className="text-orange-600" size={24} />
                  <h2 className="text-xl font-bold text-gray-800 dark:text-white">
                    Utilisateurs en attente d'approbation
                  </h2>
                  {pendingUsers.length > 0 && (
                    <span className="px-3 py-1 bg-red-500 text-white text-sm font-semibold rounded-full animate-pulse">
                      {pendingUsers.length}
                    </span>
                  )}
                </div>

                {pendingUsers.length === 0 ? (
                  <div className="text-center py-8 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                    <UserCheck className="mx-auto text-gray-400 mb-2" size={48} />
                    <p className="text-gray-600 dark:text-gray-400">
                      Aucun utilisateur en attente
                    </p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {pendingUsers.map((user) => (
                      <div key={user.id} className="bg-white dark:bg-gray-800 border border-orange-200 dark:border-orange-800 rounded-lg p-4 shadow-sm">
                        <div className="flex items-center justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-3 mb-2">
                              <div className="w-10 h-10 bg-orange-100 dark:bg-orange-900/30 rounded-full flex items-center justify-center">
                                <Users className="text-orange-600 dark:text-orange-400" size={20} />
                              </div>
                              <div>
                                <h3 className="font-semibold text-gray-800 dark:text-white">
                                  {user.name}
                                </h3>
                                <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
                                  <Mail size={14} />
                                  {user.email}
                                </div>
                              </div>
                            </div>

                            {user.phone && (
                              <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400 ml-13">
                                <Phone size={14} />
                                {user.phone}
                              </div>
                            )}

                            {user.fonction && (
                              <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400 ml-13">
                                <Briefcase size={14} />
                                {user.fonction}
                              </div>
                            )}

                            <div className="mt-2 ml-13">
                              <span className="text-xs text-gray-500 dark:text-gray-400">
                                Inscrit le {new Date(user.createdAt?.toDate ? user.createdAt.toDate() : user.createdAt).toLocaleDateString('fr-FR')}
                              </span>
                            </div>
                          </div>

                          <div className="flex gap-2">
                            <button
                              onClick={() => handleApprove(user.id)}
                              disabled={loading}
                              className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                              title="Approuver"
                            >
                              <Check size={18} />
                              Approuver
                            </button>

                            <button
                              onClick={() => handleReject(user.id)}
                              disabled={loading}
                              className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                              title="Rejeter"
                            >
                              <X size={18} />
                              Rejeter
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Utilisateurs approuvés */}
              <div>
                <div className="flex items-center gap-2 mb-4">
                  <UserCheck className="text-green-600" size={24} />
                  <h2 className="text-xl font-bold text-gray-800 dark:text-white">
                    Utilisateurs approuvés
                  </h2>
                  <span className="px-3 py-1 bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400 text-sm font-semibold rounded-full">
                    {approvedUsers.length}
                  </span>
                </div>

                {approvedUsers.length === 0 ? (
                  <div className="text-center py-8 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                    <UserX className="mx-auto text-gray-400 mb-2" size={48} />
                    <p className="text-gray-600 dark:text-gray-400">
                      Aucun utilisateur approuvé
                    </p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-gray-50 dark:bg-gray-700/50">
                        <tr>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                            Utilisateur
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                            Contact
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                            Rôle
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                            Actions
                          </th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                        {approvedUsers.map((user) => (
                          <tr key={user.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/30">
                            <td className="px-4 py-4">
                              <div className="flex items-center gap-3">
                                <div className="w-10 h-10 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center">
                                  <Users className="text-green-600 dark:text-green-400" size={20} />
                                </div>
                                <div>
                                  <div className="font-semibold text-gray-800 dark:text-white">
                                    {user.name}
                                  </div>
                                  {user.fonction && (
                                    <div className="text-xs text-gray-500 dark:text-gray-400">
                                      {user.fonction}
                                    </div>
                                  )}
                                </div>
                              </div>
                            </td>
                            <td className="px-4 py-4">
                              <div className="text-sm text-gray-600 dark:text-gray-400">
                                <div className="flex items-center gap-1 mb-1">
                                  <Mail size={14} />
                                  {user.email}
                                </div>
                                {user.phone && (
                                  <div className="flex items-center gap-1">
                                    <Phone size={14} />
                                    {user.phone}
                                  </div>
                                )}
                              </div>
                            </td>
                            <td className="px-4 py-4">
                              <select
                                value={user.role}
                                onChange={(e) => handleRoleChange(user.id, e.target.value)}
                                disabled={loading || user.id === currentUser.id}
                                className={`px-3 py-1 rounded-full text-sm font-semibold ${getRoleColor(user.role)} border-0 disabled:cursor-not-allowed`}
                              >
                                <option value="membre">Membre</option>
                                <option value="secretaire">Secrétaire</option>
                                <option value="tresorier">Trésorier</option>
                                <option value="pasteur">Pasteur</option>
                                <option value="president">Président</option>
                                <option value="admin">Administrateur</option>
                              </select>
                            </td>
                            <td className="px-4 py-4">
                              {user.id !== currentUser.id && (
                                <button
                                  onClick={() => handleDelete(user.id)}
                                  disabled={loading}
                                  className="p-2 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                  title="Supprimer"
                                >
                                  <Trash2 size={18} />
                                </button>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Onglet Permissions */}
          {activeTab === 'permissions' && hasPermission(currentUser, 'manage_users') && (
            <ModulePermissionsTab />
          )}

          {/* Onglet Église */}
          {activeTab === 'church' && hasPermission(currentUser, 'manage_users') && (
            <ChurchSettingsTab />
          )}
        </div>
      </div>
    </div>
  );
};

export default Settings;